/**
 * Session Management Utilities
 *
 * Handles session expiration detection and management
 * Requirements: 12.1, 12.2, 12.3, 12.4
 */

import type { Session } from '@supabase/supabase-js';

/**
 * Check if a session is expired or about to expire
 * @param session - The Supabase session to check
 * @param bufferSeconds - Number of seconds before expiration to consider expired (default: 60)
 * @returns true if session is expired or about to expire
 */
export const isSessionExpired = (session: Session | null, bufferSeconds: number = 60): boolean => {
  if (!session) {
    return true;
  }

  const expiresAt = session.expires_at;
  if (!expiresAt) {
    return false;
  }

  const now = Math.floor(Date.now() / 1000);
  const expiresIn = expiresAt - now;

  return expiresIn <= bufferSeconds;
};

/**
 * Get the time remaining until session expiration
 * @param session - The Supabase session to check
 * @returns seconds until expiration, or null if no expiration time
 */
export const getSessionTimeRemaining = (session: Session | null): number | null => {
  if (!session?.expires_at) {
    return null;
  }

  const now = Math.floor(Date.now() / 1000);
  const expiresIn = session.expires_at - now;

  return Math.max(0, expiresIn);
};

/**
 * Clear all authentication data from storage
 * Requirements: 12.4 - Clear session on logout
 */
export const clearAuthData = (): void => {
  // Clear Supabase auth data from localStorage
  // Supabase stores session data with keys prefixed by 'sb-'
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('sb-')) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));

  // Clear any legacy auth tokens
  localStorage.removeItem('supabase.auth.token');

  // Clear session storage
  sessionStorage.clear();

  console.log('[SessionManager] Cleared all authentication data');
};

/**
 * Store session token in secure storage
 * Requirements: 12.1 - Store session token
 * Note: Supabase handles this automatically via localStorage
 */
export const storeSession = (_session: Session): void => {
  // Supabase automatically stores the session in localStorage
  // This function is provided for explicit documentation and future extensibility
  console.log('[SessionManager] Session stored (handled by Supabase)');
};

/**
 * Restore session from storage
 * Requirements: 12.2 - Restore session on app initialization
 * Note: Supabase handles this automatically via getSession()
 */
export const restoreSession = async (): Promise<Session | null> => {
  // Supabase automatically restores the session from localStorage
  // This function is provided for explicit documentation and future extensibility
  console.log('[SessionManager] Session restoration (handled by Supabase)');
  return null;
};
